"use client";

import {
  Box,
  Button,
  Flex,
  Image,
  Input,
  Textarea,
  Stack,
  Text,
} from "@chakra-ui/react";

import PhoneInput from "@components/common/PhoneInput";
import html2canvas from "html2canvas";
import { isValidPhone } from "lib/phoneUtils";
import { QRCodeSVG } from "qrcode.react";
import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import { LuDownload, LuLocateFixed, LuPrinter } from "react-icons/lu";

import {
  formatGhanaPostInput,
  isValidGhanaPostCode,
} from "@features/services/shipping/deliveryAddress";
import { classifyGoods } from "@features/services/shipping/rates";
import { formatTrackingRefForDisplay } from "@features/services/shipping/trackingRefFormat";

const MONO_FONT = "'Share Tech Mono', monospace";

const INTERNAL_USE_LABEL = "Internal use only - Pkg #";

const capitalizeWords = (s: string) =>
  s.replace(/(^|\s)\S/g, (c) => c.toUpperCase());

// ---------------------------------------------------------------------------
// SenderForm
// ---------------------------------------------------------------------------

export interface SenderInfo {
  name: string;
  phone: string;
  yob: string;
  area: string;
  landmark: string;
  ghanaPostCode: string;
  lat: number | null;
  lng: number | null;
  goodsDescription: string;
}

interface SenderFormProps {
  onSubmit: (sender: SenderInfo) => void;
  submitting: boolean;
  defaultName?: string;
  // Total CBM of the uploaded packing list, when one exists — lets the rate
  // hint classify with volume so size-dependent escalations match the
  // estimate computed from the same list.
  cargoCbm?: number | null;
  // Rendered at the bottom of the form, just above the submit button.
  extraContent?: ReactNode;
}

export const SenderForm = ({
  onSubmit,
  submitting,
  defaultName,
  cargoCbm,
  extraContent,
}: SenderFormProps) => {
  const [sender, setSender] = useState<SenderInfo>({
    name: defaultName ?? "",
    phone: "",
    yob: "",
    area: "",
    landmark: "",
    ghanaPostCode: "",
    lat: null,
    lng: null,
    goodsDescription: "",
  });
  const [locationStatus, setLocationStatus] = useState<
    "idle" | "locating" | "captured" | "error"
  >("idle");

  const onChangeField = useMemo(() => {
    const handler =
      (field: keyof SenderInfo) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setSender((prev) => ({ ...prev, [field]: e.target.value }));
      };
    return {
      name: handler("name"),
      yob: handler("yob"),
      area: handler("area"),
    };
  }, []);

  const [phoneBlurred, setPhoneBlurred] = useState(false);
  const [ghanaPostBlurred, setGhanaPostBlurred] = useState(false);

  const onPhoneChange = useCallback((val: string) => {
    setSender((prev) => ({ ...prev, phone: val }));
  }, []);

  const onPhoneBlur = useCallback(() => {
    setPhoneBlurred(true);
  }, []);

  const onGhanaPostChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSender((prev) => ({
        ...prev,
        ghanaPostCode: formatGhanaPostInput(e.target.value),
      }));
    },
    [],
  );

  const onGhanaPostBlur = useCallback(() => {
    setGhanaPostBlurred(true);
  }, []);

  const onGoodsDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setSender((prev) => ({ ...prev, goodsDescription: e.target.value }));
    },
    [],
  );

  const onLandmarkChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setSender((prev) => ({ ...prev, landmark: e.target.value }));
    },
    [],
  );

  const onUseMyLocation = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setLocationStatus("error");
      return;
    }
    setLocationStatus("locating");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setSender((prev) => ({
          ...prev,
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        }));
        setLocationStatus("captured");
      },
      () => setLocationStatus("error"),
      { enableHighAccuracy: true, timeout: 15000 },
    );
  }, []);

  const phoneValid = isValidPhone(sender.phone);
  const showPhoneError = phoneBlurred && sender.phone !== "" && !phoneValid;
  // GhanaPost is optional — most customers don't know theirs, and requiring
  // it produced fabricated codes. Validate only when one is entered.
  const ghanaPostValid =
    sender.ghanaPostCode === "" || isValidGhanaPostCode(sender.ghanaPostCode);
  const showGhanaPostError =
    ghanaPostBlurred && sender.ghanaPostCode !== "" && !ghanaPostValid;

  const canSubmit =
    sender.name.trim().length > 0 &&
    phoneValid &&
    sender.yob.trim().length > 0 &&
    sender.area.trim().length > 0 &&
    sender.landmark.trim().length > 0 &&
    ghanaPostValid &&
    sender.goodsDescription.trim().length > 0;

  const goodsRateHint = useMemo(() => {
    const description = sender.goodsDescription.trim();
    if (description === "") {
      return null;
    }
    const { category, matchedKeyword, perPieceQuote } = classifyGoods(
      description,
      cargoCbm,
    );
    if (perPieceQuote) {
      return "These goods are quoted per piece — we'll contact you with a price.";
    }
    if (matchedKeyword == null) {
      return null;
    }
    return `Estimated rate: $${category.ratePerCbm}/CBM (${category.label})`;
  }, [sender.goodsDescription, cargoCbm]);

  const onClickSubmit = useCallback(() => {
    onSubmit({
      ...sender,
      name: capitalizeWords(sender.name.trim()),
      area: sender.area.trim(),
      landmark: sender.landmark.trim(),
      goodsDescription: sender.goodsDescription.trim(),
    });
  }, [onSubmit, sender]);

  return (
    <Stack gap={4}>
      <Stack gap={1}>
        <Text fontSize="xs" color="text.secondary">
          Enter your name
        </Text>
        <Input
          size="sm"
          placeholder="Your full name"
          value={sender.name}
          onChange={onChangeField.name}
        />
      </Stack>
      <Stack gap={1}>
        <Text fontSize="xs" color="text.secondary">
          Phone number
        </Text>
        <PhoneInput
          size="sm"
          value={sender.phone}
          onChange={onPhoneChange}
          onBlur={onPhoneBlur}
        />
        {showPhoneError && (
          <Text fontSize="xs" color="fg.error">
            Enter a valid phone number
          </Text>
        )}
      </Stack>
      <Stack gap={1}>
        <Text fontSize="xs" color="text.secondary">
          Year of birth
        </Text>
        <Input
          size="sm"
          type="number"
          placeholder="e.g. 1990"
          _placeholder={{ color: "text.secondary" }}
          value={sender.yob}
          onChange={onChangeField.yob}
        />
      </Stack>
      <Stack gap={1}>
        <Text fontSize="xs" color="text.secondary">
          Area / town
        </Text>
        <Input
          size="sm"
          placeholder="e.g. Kasoa, Tema Community 18"
          _placeholder={{ color: "text.secondary" }}
          borderColor="border.light"
          value={sender.area}
          onChange={onChangeField.area}
        />
      </Stack>
      <Stack gap={1}>
        <Text fontSize="xs" color="text.secondary">
          Directions / landmark
        </Text>
        <Textarea
          size="sm"
          rows={2}
          placeholder="e.g. Behind the blue kiosk after the old barrier"
          _placeholder={{ color: "text.secondary" }}
          borderColor="border.light"
          maxLength={300}
          value={sender.landmark}
          onChange={onLandmarkChange}
        />
      </Stack>
      <Flex align="center" gap={2}>
        <Button
          size="xs"
          variant="surfaceOutline"
          onClick={onUseMyLocation}
          loading={locationStatus === "locating"}
          disabled={locationStatus === "captured"}
        >
          <LuLocateFixed />
          {locationStatus === "captured"
            ? "Location captured"
            : "Use my current location"}
        </Button>
        {locationStatus === "captured" && (
          <Text fontSize="xs" color="text.secondary">
            ✓ Exact pin saved for delivery
          </Text>
        )}
        {locationStatus === "error" && (
          <Text fontSize="xs" color="fg.error">
            Couldn&apos;t get your location
          </Text>
        )}
      </Flex>
      <Stack gap={1}>
        <Text fontSize="xs" color="text.secondary">
          Ghana Post GPS address (optional)
        </Text>
        <Input
          size="sm"
          placeholder="e.g. GS-1234-5678 — skip if you don't know it"
          _placeholder={{ color: "text.secondary" }}
          borderColor="border.light"
          value={sender.ghanaPostCode}
          onChange={onGhanaPostChange}
          onBlur={onGhanaPostBlur}
        />
        {showGhanaPostError && (
          <Text fontSize="xs" color="fg.error">
            Enter a valid Ghana Post GPS code (e.g. GA-183-8164) or leave blank
          </Text>
        )}
      </Stack>
      <Stack gap={1}>
        <Text fontSize="xs" color="text.secondary">
          Description of goods
        </Text>
        <Textarea
          size="sm"
          rows={2}
          placeholder="e.g. 10 boxes of LED bulbs and CCTV cameras"
          _placeholder={{ color: "text.secondary" }}
          borderColor="border.light"
          maxLength={500}
          value={sender.goodsDescription}
          onChange={onGoodsDescriptionChange}
        />
        {goodsRateHint != null && (
          <Text fontSize="xs" color="text.secondary">
            {goodsRateHint}
          </Text>
        )}
      </Stack>
      {extraContent}
      <Button
        size="sm"
        w="100%"
        onClick={onClickSubmit}
        disabled={!canSubmit || submitting}
        loading={submitting}
        loadingText="Saving"
      >
        Get your shipping label
      </Button>
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// ShippingLabel
// ---------------------------------------------------------------------------

const DEFAULT_ADDRESS =
  "广州市白云区南岭南业八横路东三巷10号\n收货人：梁先生，\n电话：190 4712 3965";

interface ShippingLabelProps {
  trackingRef: string;
  warehouseAddress: string | null;
  customerName: string | null;
  goodsDescription?: string | null;
}

const escapeHtml = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;");

export const ShippingLabel = ({
  trackingRef,
  warehouseAddress,
  customerName,
  goodsDescription,
}: ShippingLabelProps) => {
  const labelRef = useRef<HTMLDivElement>(null);
  const address = warehouseAddress ?? DEFAULT_ADDRESS;
  const displayTrackingRef = useMemo(
    () => formatTrackingRefForDisplay(trackingRef, "ELK"),
    [trackingRef],
  );

  const addressLines = useMemo(() => address.split("\n"), [address]);

  const addressHtml = useMemo(
    () => addressLines.map(escapeHtml).join("<br/>"),
    [addressLines],
  );

  const displayName = useMemo(
    () => (customerName != null ? capitalizeWords(customerName) : null),
    [customerName],
  );

  const customerNameHtml = useMemo(
    () => (displayName != null ? escapeHtml(displayName) : null),
    [displayName],
  );

  const goodsHtml = useMemo(
    () =>
      goodsDescription != null && goodsDescription !== ""
        ? escapeHtml(goodsDescription)
        : null,
    [goodsDescription],
  );

  const onSaveAsImage = useCallback(async () => {
    if (labelRef.current == null) {
      return;
    }
    const canvas = await html2canvas(labelRef.current, {
      backgroundColor: "#ffffff",
      scale: 3,
    });
    const link = document.createElement("a");
    link.download = `ELK-CARGO-${trackingRef}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  }, [trackingRef]);

  const onPrint = useCallback(() => {
    if (labelRef.current == null) {
      return;
    }
    const qrSvg = labelRef.current.querySelector("svg");
    const qrHtml = qrSvg != null ? qrSvg.outerHTML : "";
    const html = `
      <html>
        <head>
          <meta charset="UTF-8">
          <title>ELK CARGO - ${displayTrackingRef}</title>
          <style>
            body {
              margin: 0;
              display: flex;
              justify-content: center;
              align-items: center;
              min-height: 100vh;
            }
            .label {
              padding: 48px;
              font-family: Arial, Helvetica, "PingFang SC", "Microsoft YaHei", "Noto Sans SC", sans-serif;
              border: 2px solid #000;
              width: 420px;
            }
            .logo {
              height: 16px;
              margin-bottom: 16px;
            }
            .tracking-ref {
              font-size: 22px;
              font-weight: 700;
              margin-bottom: 20px;
              letter-spacing: 0.05em;
            }
            .customer {
              font-size: 16px;
              font-weight: 700;
              margin-bottom: 16px;
            }
            .goods {
              font-size: 14px;
              color: #333;
              margin-bottom: 16px;
            }
            .address {
              font-size: 16px;
              line-height: 1.8;
              white-space: pre-line;
            }
            .qr-code {
              margin-top: 20px;
              display: flex;
              justify-content: center;
            }
            .internal-use {
              margin-top: 24px;
              padding: 8px 10px;
              border: 1px dashed #999;
              border-radius: 4px;
              font-size: 11px;
              color: #555;
              display: flex;
              justify-content: space-between;
              align-items: center;
              gap: 8px;
            }
            .internal-use .label-text {
              white-space: nowrap;
            }
            .internal-use .field {
              flex: 1;
              border-bottom: 1px solid #555;
              height: 16px;
            }
          </style>
        </head>
        <body>
          <div class="label">
            <img class="logo" src="/images/logo.png" alt="ELK Cargo" />
            <div class="tracking-ref">ELK CARGO - ${displayTrackingRef}</div>
            ${
              customerNameHtml != null
                ? `<div class="customer">${customerNameHtml}</div>`
                : ""
            }
            ${goodsHtml != null ? `<div class="goods">${goodsHtml}</div>` : ""}
            <div class="address">${addressHtml}</div>
            <div class="qr-code">${qrHtml}</div>
            <div class="internal-use">
              <span class="label-text">${INTERNAL_USE_LABEL}</span>
              <span class="field"></span>
            </div>
          </div>
        </body>
      </html>
    `;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url, "_blank");
    if (printWindow == null) {
      URL.revokeObjectURL(url);
      return;
    }
    printWindow.onload = () => {
      printWindow.print();
      URL.revokeObjectURL(url);
    };
  }, [displayTrackingRef, addressHtml, customerNameHtml, goodsHtml]);

  return (
    <>
      <Box
        ref={labelRef}
        border="2px solid"
        borderColor="#333"
        borderRadius="4px"
        bg="#ffffff"
        color="#000000"
        p="40px"
      >
        <Image src="/images/logo.png" alt="ELK Cargo" h="16px" mb="16px" />
        <Text
          fontSize="20px"
          fontWeight={700}
          letterSpacing=".05em"
          mb="16px"
          fontFamily={MONO_FONT}
        >
          ELK CARGO - {displayTrackingRef}
        </Text>
        {displayName != null && (
          <Text fontSize="15px" fontWeight={700} mb="14px">
            {displayName}
          </Text>
        )}
        {goodsDescription != null && goodsDescription !== "" && (
          <Text fontSize="13px" color="#333333" mb="14px">
            {goodsDescription}
          </Text>
        )}
        <Box fontSize="15px" lineHeight="1.8">
          {addressLines.map((line, i) => (
            <Text key={i}>{line}</Text>
          ))}
        </Box>
        <Box mt="20px" display="flex" justifyContent="center">
          <QRCodeSVG
            value={`${window.location.origin}/scan?service=shipping&ref=${trackingRef}`}
            size={100}
          />
        </Box>
        <Flex
          mt="24px"
          p="8px 10px"
          border="1px dashed"
          borderColor="#999"
          borderRadius="4px"
          fontSize="11px"
          color="#555"
          align="center"
          gap="8px"
        >
          <Text whiteSpace="nowrap">{INTERNAL_USE_LABEL}</Text>
          <Box flex={1} borderBottom="1px solid" borderColor="#555" h="16px" />
        </Flex>
      </Box>

      <Flex gap={3} mt={5}>
        <Button
          variant="surfaceOutline"
          size="sm"
          flex={1}
          onClick={onSaveAsImage}
        >
          <LuDownload />
          Save as Image
        </Button>
        <Button variant="surfaceOutline" size="sm" flex={1} onClick={onPrint}>
          <LuPrinter />
          Print
        </Button>
      </Flex>
    </>
  );
};
